import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Download } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * The audio player on a candidate's answer.
 *
 * Two things shape this. First, seeking used to be impossible: MediaRecorder's
 * WebM declares no duration, so the old player read `el.duration` as
 * non-finite, treated it as zero, and silently disabled its own skip buttons
 * and scrub bar. Recordings are now remuxed server-side, and the measured
 * duration is passed in as a prop so the controls work even on the first
 * render, before metadata has loaded — and on any recording that somehow
 * still lacks one.
 *
 * Second, a flat progress bar tells a reviewer nothing about where the answer
 * actually is. The waveform is drawn from peaks measured server-side, so
 * silence and speech are visible and a reviewer can jump straight to the
 * substance of a four-minute answer instead of hunting through it.
 */

const SPEED_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const SKIP_SECONDS = 15;
const WAVE_BARS = 100;

const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

/** Downsample server peaks to the number of bars we actually draw. */
function toBars(peaks) {
    if (!Array.isArray(peaks) || peaks.length === 0) return null;
    if (peaks.length <= WAVE_BARS) return peaks;
    const per = peaks.length / WAVE_BARS;
    return Array.from({ length: WAVE_BARS }, (_, i) => {
        const slice = peaks.slice(Math.floor(i * per), Math.max(Math.floor((i + 1) * per), Math.floor(i * per) + 1));
        return slice.length ? Math.max(...slice) : 0;
    });
}

export default function ResponseAudioPlayer({ src, durationSeconds = null, peaks = null, downloadName }) {
    const audioRef = useRef(null);
    const trackRef = useRef(null);
    const rafRef   = useRef(null);
    // While dragging, the thumb follows the pointer and the audio is left
    // alone — committing on release only. Seeking on every pointermove makes
    // the browser stutter through decode on long recordings.
    const draggingRef = useRef(false);

    const [playing, setPlaying]   = useState(false);
    const [current, setCurrent]   = useState(0);
    const [duration, setDuration] = useState(Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 0);
    const [buffered, setBuffered] = useState(0);
    const [speedIdx, setSpeedIdx] = useState(2);
    const [dragPos, setDragPos]   = useState(null);

    const speed = SPEED_STEPS[speedIdx];
    const bars  = toBars(peaks);
    const pct   = dragPos != null ? dragPos * 100 : (duration > 0 ? (current / duration) * 100 : 0);

    /* ── smooth playhead ──────────────────────────────────────────────────
       `timeupdate` fires roughly four times a second, which makes the
       playhead visibly lurch. While playing we read currentTime on every
       frame instead, and stop the loop the moment playback does. */
    useEffect(() => {
        if (!playing) return undefined;
        const tick = () => {
            const el = audioRef.current;
            if (el && !draggingRef.current) setCurrent(el.currentTime || 0);
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [playing]);

    /* ── only one answer plays at a time ─────────────────────────────────── */
    useEffect(() => {
        const onOtherPlay = (e) => { if (e.detail !== audioRef.current) setPlaying(false); };
        document.addEventListener('ra:play', onOtherPlay);
        return () => document.removeEventListener('ra:play', onOtherPlay);
    }, []);

    const seekTo = useCallback((seconds) => {
        const el = audioRef.current;
        if (!el || !(duration > 0)) return;
        const next = Math.max(0, Math.min(duration, seconds));
        el.currentTime = next;
        setCurrent(next);
    }, [duration]);

    const toggle = () => {
        const el = audioRef.current;
        if (!el) return;
        if (playing) { el.pause(); setPlaying(false); return; }

        document.querySelectorAll('audio[data-ra]').forEach(a => { if (a !== el) a.pause(); });
        document.dispatchEvent(new CustomEvent('ra:play', { detail: el }));
        el.play()
            .then(() => setPlaying(true))
            .catch((err) => {
                // AbortError just means a rapid double-click interrupted play().
                if (err?.name === 'AbortError') return;
                setPlaying(false);
                toast.error('Unable to play this recording.', { duration: 3500 });
            });
    };

    const cycleSpeed = () => {
        setSpeedIdx(idx => {
            const next = (idx + 1) % SPEED_STEPS.length;
            if (audioRef.current) audioRef.current.playbackRate = SPEED_STEPS[next];
            return next;
        });
    };

    /* ── drag to scrub ────────────────────────────────────────────────────
       Pointer capture is what makes this feel right: once the drag starts the
       track keeps receiving moves even when the pointer leaves it, so a
       reviewer dragging along a 2px-tall bar does not lose the handle the
       moment their hand drifts. */
    const ratioFromEvent = (e) => {
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return 0;
        return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    };

    const onPointerDown = (e) => {
        if (!(duration > 0)) return;
        e.preventDefault();
        draggingRef.current = true;
        trackRef.current?.setPointerCapture?.(e.pointerId);
        setDragPos(ratioFromEvent(e));
    };

    const onPointerMove = (e) => {
        if (!draggingRef.current) return;
        setDragPos(ratioFromEvent(e));
    };

    const endDrag = (e) => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        trackRef.current?.releasePointerCapture?.(e.pointerId);
        const ratio = ratioFromEvent(e);
        setDragPos(null);
        seekTo(ratio * duration);
    };

    /* ── keyboard ─────────────────────────────────────────────────────────
       The old scrub bar was a plain div with an onClick: unreachable by
       keyboard entirely. Arrow keys move by a second, so a reviewer can land
       on an exact word without fighting the mouse. */
    const onKeyDown = (e) => {
        if (!(duration > 0)) return;
        const jumps = {
            ArrowLeft: -1, ArrowRight: 1,
            PageDown: -SKIP_SECONDS, PageUp: SKIP_SECONDS,
        };
        if (e.key in jumps)      { e.preventDefault(); seekTo(current + jumps[e.key]); }
        else if (e.key === 'Home') { e.preventDefault(); seekTo(0); }
        else if (e.key === 'End')  { e.preventDefault(); seekTo(duration); }
        else if (e.key === ' ')    { e.preventDefault(); toggle(); }
    };

    const seekable = duration > 0;

    return (
        <div className="rounded-lg border border-border bg-muted/20 px-2.5 py-2 space-y-2">
            {/* Controls */}
            <div className="flex items-center gap-1.5">
                <button
                    type="button" onClick={() => seekTo(current - SKIP_SECONDS)}
                    disabled={!seekable} title={`Back ${SKIP_SECONDS} seconds`}
                    aria-label={`Back ${SKIP_SECONDS} seconds`}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                >
                    <SkipBack className="h-3.5 w-3.5" aria-hidden />
                </button>

                <button
                    type="button" onClick={toggle}
                    aria-label={playing ? 'Pause' : 'Play'}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
                >
                    {playing ? <Pause className="h-3 w-3" aria-hidden /> : <Play className="ml-0.5 h-3 w-3" aria-hidden />}
                </button>

                <button
                    type="button" onClick={() => seekTo(current + SKIP_SECONDS)}
                    disabled={!seekable} title={`Forward ${SKIP_SECONDS} seconds`}
                    aria-label={`Forward ${SKIP_SECONDS} seconds`}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                >
                    <SkipForward className="h-3.5 w-3.5" aria-hidden />
                </button>

                <span className="w-[76px] shrink-0 text-[10px] tabular-nums text-muted-foreground">
                    {fmt(dragPos != null ? dragPos * duration : current)} / {fmt(duration)}
                </span>

                <a
                    href={src} download={downloadName || true} title="Download recording"
                    aria-label="Download recording"
                    className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <Download className="h-3.5 w-3.5" aria-hidden />
                </a>

                <button
                    type="button" onClick={cycleSpeed} title="Playback speed"
                    className="shrink-0 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
                >
                    {speed === 1 ? '1×' : `${speed}×`}
                </button>
            </div>

            {/* Waveform / scrub track */}
            <div
                ref={trackRef}
                role="slider"
                tabIndex={seekable ? 0 : -1}
                aria-label="Seek within recording"
                aria-valuemin={0}
                aria-valuemax={Math.round(duration)}
                aria-valuenow={Math.round(dragPos != null ? dragPos * duration : current)}
                aria-valuetext={`${fmt(dragPos != null ? dragPos * duration : current)} of ${fmt(duration)}`}
                aria-disabled={!seekable}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onKeyDown={onKeyDown}
                className={cn(
                    'group relative select-none rounded outline-none',
                    // A real hit target. The old bar was 2px of paint inside an
                    // 8px box with an invisible thumb — technically clickable,
                    // practically not.
                    bars ? 'h-9' : 'h-6',
                    seekable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
                    'focus-visible:ring-2 focus-visible:ring-primary/60'
                )}
            >
                {bars ? (
                    <div className="pointer-events-none absolute inset-0 flex items-center gap-px">
                        {bars.map((p, i) => {
                            const played = (i / bars.length) * 100 < pct;
                            return (
                                <span
                                    key={i}
                                    className={cn(
                                        'flex-1 rounded-[1px] transition-colors',
                                        played ? 'bg-primary' : 'bg-muted-foreground/25'
                                    )}
                                    // Floor keeps quiet passages visible as a hairline
                                    // rather than vanishing into the background.
                                    style={{ height: `${Math.max(8, p * 100)}%` }}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className="pointer-events-none absolute inset-y-[9px] inset-x-0 overflow-hidden rounded-full bg-muted">
                        <div className="absolute inset-y-0 left-0 bg-muted-foreground/20" style={{ width: `${buffered}%` }} />
                        <div className="absolute inset-y-0 left-0 rounded-full bg-primary/70" style={{ width: `${pct}%` }} />
                    </div>
                )}

                {/* Playhead — always visible while dragging, on hover otherwise */}
                {seekable && (
                    <div
                        className={cn(
                            'pointer-events-none absolute top-0 bottom-0 w-0.5 -translate-x-1/2 rounded-full bg-primary transition-opacity',
                            dragPos != null ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
                        )}
                        style={{ left: `${pct}%` }}
                    >
                        <span className="absolute -top-0.5 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-primary shadow" />
                    </div>
                )}
            </div>

            <audio
                ref={audioRef}
                data-ra
                src={src}
                preload="metadata"
                onLoadedMetadata={() => {
                    const el = audioRef.current;
                    if (!el) return;
                    // Only trust the element when the server gave us nothing:
                    // an un-remuxed WebM reports Infinity here.
                    if (!(duration > 0) && Number.isFinite(el.duration) && el.duration > 0) {
                        setDuration(el.duration);
                    }
                    el.playbackRate = speed;
                }}
                onProgress={() => {
                    const el = audioRef.current;
                    if (!el || !(duration > 0) || el.buffered.length === 0) return;
                    setBuffered(Math.min(100, (el.buffered.end(el.buffered.length - 1) / duration) * 100));
                }}
                onTimeUpdate={() => {
                    // Backstop for when rAF is not running (paused, or a seek
                    // while stopped).
                    const el = audioRef.current;
                    if (el && !playing && !draggingRef.current) setCurrent(el.currentTime || 0);
                }}
                onEnded={() => { setPlaying(false); setCurrent(0); }}
                onPause={() => setPlaying(false)}
                onError={() => { setPlaying(false); toast.error('Unable to load this recording.', { duration: 3500 }); }}
                className="hidden"
            />
        </div>
    );
}
